import React, { useState, useEffect } from "react";
import { X, Sparkles, Wrench, Bug, ChevronRight } from "lucide-react";
import { API } from "../context";

const CATEGORY_CONFIG = {
  feature: { label: "New", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", icon: Sparkles },
  improvement: { label: "Improved", color: "bg-blue-500/20 text-blue-300 border-blue-500/30", icon: Wrench },
  fix: { label: "Fixed", color: "bg-amber-500/20 text-amber-300 border-amber-500/30", icon: Bug },
};

export default function WhatsNewModal({ onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/changelog/unseen`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setEntries(data.entries || []);
      } catch {}
      setLoading(false);
    })();
  }, [token]);

  const handleDismiss = async () => {
    if (token) {
      try {
        await fetch(`${API}/changelog/mark-seen`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {}
    }
    onClose();
  };

  if (loading || entries.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="whats-new-modal">
      <div className="absolute inset-0 bg-black/60" onClick={handleDismiss} />
      <div className="relative w-full max-w-md bg-gradient-to-b from-slate-900 to-slate-950 rounded-2xl border border-white/10 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 border-b border-white/5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-fredoka font-bold text-white flex items-center gap-2" data-testid="whats-new-title">
                <Sparkles size={18} className="text-amber-400" />
                What's New
              </h2>
              <p className="text-white/40 text-xs mt-1">{entries.length} update{entries.length !== 1 ? "s" : ""} since your last visit</p>
            </div>
            <button onClick={handleDismiss} className="p-1.5 rounded-lg hover:bg-white/5 text-white/40 hover:text-white transition-colors" data-testid="whats-new-close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Entries */}
        <div className="px-6 py-4 max-h-[50vh] overflow-y-auto space-y-3 scrollbar-thin scrollbar-thumb-white/10" data-testid="whats-new-entries">
          {entries.map((entry, i) => {
            const cat = CATEGORY_CONFIG[entry.category] || CATEGORY_CONFIG.feature;
            const CatIcon = cat.icon;
            return (
              <div key={entry.id || i} className="p-3 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors" data-testid={`changelog-entry-${i}`}>
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 mt-0.5 px-2 py-0.5 rounded-md text-[10px] font-bold border ${cat.color}`}>
                    <CatIcon size={10} className="inline mr-1" />{cat.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <h3 className="text-white text-sm font-bold truncate">{entry.title}</h3>
                      {entry.version && <span className="text-white/20 text-[10px] flex-shrink-0">v{entry.version}</span>}
                    </div>
                    <p className="text-white/40 text-xs mt-1 leading-relaxed">{entry.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/5">
          <button onClick={handleDismiss} className="w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-fredoka font-bold text-sm transition-all flex items-center justify-center gap-2" data-testid="whats-new-got-it">
            Got it <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
